<template>
  <div>
    <div
      v-if="loading"
      class="fixed top-42 left-108 z-10 grid place-items-center"
      :style="{
        background: 'rgba(255,255,255, 0.5)',
        width: 'calc(100vw - 22rem - 5rem - 0.5rem)',
        height: 'calc(100vh - 13.5rem - 0.5rem)'
      }"
    >
      <div class="blum-loader"></div>
    </div>
    <div class="h-12 flex items-end sticky z-20 top-30 bg-slate-200">
      <div
        class="h-12 w-20 sticky z-10 left-88 flex-shrink-0 bg-slate-200 border-b border-slate-400"
      ></div>
      <div
        v-for="(n, idx) in headers"
        :key="idx"
        class="py-1 flex-shrink-0 flex items-center justify-center border-b border-slate-400"
        :style="{ width: `calc(${slotWidth}` }"
      >
        <div v-if="n.highlight" class="h-7 flex items-center gap-1">
          <BlTextBody2 :text="n.label.split(' ')[0]" />
          <div
            class="h-7 w-7 flex items-center justify-center rounded-full bg-light-purple-400"
          >
            <BlTextBody2
              :text="n.label.split(' ').pop()"
              class="text-pure-white"
            />
          </div>
        </div>
        <div v-else class="h-7 truncate px-4" :title="n.label">
          <BlTextBody2 :text="n.label" class="truncate" />
        </div>
      </div>
    </div>
    <div class="flex flex-col relative bg-slate-200">
      <div
        v-for="time in clockRound"
        :key="time"
        class="flex"
        :id="`time_view_${time}`"
      >
        <div
          class="w-20 py-2 px-3 flex-shrink-0 sticky z-10 left-88 bg-slate-200 flex justify-end border-b border-r border-slate-400 transition-all duration-300"
          :style="{ height: `${slotHeight * 2}rem` }"
        >
          <BlTextBody2 :text="time" />
        </div>
        <div
          v-for="(n, idx) in headers"
          :key="idx"
          class="flex-shrink-0 flex flex-col gap-2 items-center justify-center border-b border-r border-slate-400 bg-slate-200 transition-all duration-300"
          :style="{
            height: `${slotHeight * 2}rem`,
            width: `calc(${slotWidth}`
          }"
        ></div>
      </div>
      <div
        v-if="!loading"
        class="absolute flex items-center"
        :style="{
          opacity: currentTime.opacity,
          top: currentTime.top,
          left: `calc(${currentTime.left}`,
          zIndex: 1
        }"
      >
        <div class="rounded h-2 w-2 flex-shrink-0 bg-red-cherry"></div>
        <div
          class="h-1 bg-red-cherry"
          :style="{ width: `calc(${currentTime.width}` }"
        ></div>
      </div>
      <CalendarSlot
        v-for="(item, idx) in slots"
        :key="idx"
        :isCompact="isCompact"
        :period="item"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { CLOCK_ROUND } from "@/plugins/assets.js";
import moment from "moment";

import CalendarSlot from "./Slot.vue";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "MySessionTimeslots",
  components: {
    CalendarSlot,
    BlTextBody2,
    BlIcon
  },
  props: {
    isCompact: { type: Boolean, default: false },
    selectedDate: { required: false, default: null },
    headers: { type: Array, required: true },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      clockRound: CLOCK_ROUND
    };
  },
  computed: {
    ...mapGetters(["myCalendar"]),
    dimension() {
      const searchParams = this.$route.query;
      return searchParams["type"];
    },
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      if (this.isCompact) return 6.25 / 2;
      return 15 / 2;
    },
    slotWidth() {
      if (this.headers.length > 7) return "12rem";
      else return `(100vw - 22rem - 5rem - 0.5rem) / ${this.headers.length}`;
    },
    currentTime() {
      let opacity = "100%";
      const full = `${this.slotWidth} * ${this.headers.length}`;
      const oneSlot = this.slotWidth;
      let width = "0px";

      const hours = moment().format("H");
      const minutes = moment().format("mm");
      const timeInMinutes = Number(hours) * 60 + Number(minutes);
      const top = (timeInMinutes / 60) * (this.slotHeight * 2) + "rem";

      let left = `5rem`;
      if (this.dimension === "daily") {
        width = full + " - 0.5rem";
        const isSameDay =
          moment().format("YYYY-MM-DD") ===
          moment(this.selectedDate).format("YYYY-MM-DD");
        if (!isSameDay) opacity = "0%";
      }

      if (this.dimension === "weekly") {
        width = oneSlot + " - 0.5rem";
        const indexOfToday = this.headers.findIndex(i => i.highlight);
        if (indexOfToday > -1) {
          const loop = indexOfToday;
          for (let index = 0; index < loop; index++) {
            left += ` + ${oneSlot}`;
          }
        } else opacity = "0%";
      }

      return { opacity, width, top, left };
    },
    slots() {
      const arr = [];
      this.myCalendar.forEach(body => {
        const key = body.date ? body.date : null;

        body.timeslots.forEach(slot => {
          const item = { ...slot };
          const position = {
            top: "calc(",
            left: "calc(5rem"
          };
          const element = {
            height: ``,
            width: `calc(${this.slotWidth} - 2px)`,
            borderColor: item.primary_color
          };

          const indexOfTop = this.clockRound.findIndex(i => {
            const dt = i.split(":")[0];
            return dt === item.start_time_string.split(":")[0];
          });
          position.top += indexOfTop * (this.slotHeight * 2) + "rem";
          if (item.start_time_string.split(":")[1] === "30") {
            position.top += ` + ${this.slotHeight}rem`;
          }
          position.top += " + 0.5px)";

          const indexOfLeft = this.headers.findIndex(i => i.value === key);
          if (indexOfLeft > -1) {
            const loop = indexOfLeft;
            for (let index = 0; index < loop; index++) {
              position.left += ` + ${this.slotWidth}`;
            }
          } else {
            position.opacity = 0;
            position.zIndex = -1;
          }
          position.left += " + 0.5px)";

          const calculatedHeight =
            this.getSlotCount(item.start_time_string, item.end_time_string) *
            this.slotHeight;
          element.height = `calc(${calculatedHeight}rem - 2px)`;

          const isLastColumn =
            indexOfLeft === this.headers.length - 1 ||
            indexOfLeft === this.headers.length - 2;

          const res = {
            key,
            ...item,
            position,
            element,
            is_last_column: isLastColumn
          };
          arr.push(res);
        });
      });
      return arr;
    }
  },
  mounted() {
    setTimeout(() => {
      this.onScrollToTime("07:00");
    }, 1000);
  },
  methods: {
    ...mapActions(["showToast"]),
    onScrollToTime(time) {
      const id = `time_view_${time}`;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/mySessions/timeslot/Timeslots.vue